(() => {
  const SELECTOR = '[data-brand-cart-promotions]';
  const CHECKOUT_SELECTOR = '#CartDrawer-Checkout, #checkout';
  let syncInFlight = false;
  let checkoutLocked = false;
  let syncTimer;

  const setCheckoutLocked = (locked) => {
    checkoutLocked = locked;
    document.documentElement.toggleAttribute('data-brand-cart-syncing', locked);

    document.querySelectorAll(CHECKOUT_SELECTOR).forEach((button) => {
      if (locked) {
        button.dataset.brandCheckoutLabel ||= button.textContent.trim();
        button.dataset.brandCheckoutLocked = 'true';
        button.textContent = 'Updating cart…';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        return;
      }

      if (button.dataset.brandCheckoutLabel) {
        button.textContent = button.dataset.brandCheckoutLabel;
      }
      delete button.dataset.brandCheckoutLabel;
      delete button.dataset.brandCheckoutLocked;

      const cartRoot =
        button.id === 'CartDrawer-Checkout'
          ? document.querySelector('cart-drawer')
          : document.querySelector('cart-items');
      const cartIsEmpty = cartRoot?.classList.contains('is-empty') ?? false;
      button.disabled = cartIsEmpty;
      button.removeAttribute('aria-disabled');
    });
  };

  const moneyFromCents = (cents) =>
    new Intl.NumberFormat(window.Shopify?.country === 'AU' ? 'en-AU' : undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'AUD',
      maximumFractionDigits: 0,
    }).format(Math.max(0, cents) / 100);

  const normalizeOptionValue = (value) => String(value || '').toLowerCase().replace(/\s+/g, '');

  const getSettings = () => {
    const root = document.querySelector(SELECTOR);
    if (!root) return null;

    return {
      root,
      freeShippingThreshold: Number(root.dataset.freeShippingThreshold || 0),
      giftEnabled: root.dataset.gwpEnabled === 'true',
      giftThreshold: Number(root.dataset.gwpThreshold || 0),
      giftVariantId: Number(root.dataset.gwpVariantId || 0),
      productGiftEnabled: root.dataset.productGwpEnabled === 'true',
      productGiftQualifyingValue: normalizeOptionValue(root.dataset.productGwpQualifyingValue),
      productGiftVariantIds: (root.dataset.productGwpGiftVariantIds || '')
        .split(',')
        .map((id) => Number(id))
        .filter(Boolean),
    };
  };

  const getQualifyingTotal = (cart, giftVariantId) =>
    cart.items.reduce((total, item) => {
      if (item.properties?._BrandGwp) return total;
      return total + item.final_line_price;
    }, 0);

  const hasQualifyingProduct = (cart, qualifyingValue) => {
    if (!qualifyingValue) return false;

    return cart.items.some((item) => {
      if (item.properties?._BrandGwp || item.properties?._discount_kit_offer || item.parent_relationship?.parent) {
        return false;
      }

      const productHandle = String(item.handle || '').toLowerCase();
      const productTitleText = String(item.product_title || '').toLowerCase();
      const excludedHandlePattern = /(^|-)(body-mist|set|trio|bundle|discovery|gwp|gift|voucher)(-|$)/;
      const excludedTitlePattern = /\b(body mist|set|trio|bundle|discovery|gwp|gift|voucher)\b/;
      if (excludedHandlePattern.test(productHandle) || excludedTitlePattern.test(productTitleText)) return false;

      const optionValues = [
        item.variant_title,
        ...(Array.isArray(item.variant_options) ? item.variant_options : []),
        ...(Array.isArray(item.options_with_values)
          ? item.options_with_values.map((option) => option.value)
          : []),
      ];

      if (optionValues.some((value) => normalizeOptionValue(value) === qualifyingValue)) return true;

      const productType = normalizeOptionValue(item.product_type);
      const productTitle = normalizeOptionValue(productTitleText);
      const eligibleProductType = !productType || productType === 'eaudeparfum';
      return eligibleProductType && productTitle.includes(qualifyingValue);
    });
  };

  const updateProgressCopy = (cart, settings) => {
    document.querySelectorAll('[data-brand-progress]').forEach((element) => {
      const threshold = Number(element.dataset.threshold || 0);
      if (!threshold) return;

      const qualifyingTotal =
        element.dataset.brandProgress === 'gift'
          ? getQualifyingTotal(cart, settings.giftVariantId)
          : cart.total_price;
      const remaining = Math.max(threshold - qualifyingTotal, 0);
      const percent = Math.min((qualifyingTotal / threshold) * 100, 100);

      element.style.setProperty('--progress', `${percent}%`);

      const amount = element.querySelector('[data-brand-progress-remaining]');
      if (amount) amount.textContent = moneyFromCents(remaining);

      element.toggleAttribute('data-complete', remaining === 0);
    });

    const qualifyingProductPresent = hasQualifyingProduct(cart, settings.productGiftQualifyingValue);
    const productGiftsPresent =
      qualifyingProductPresent &&
      settings.productGiftVariantIds.length > 0 &&
      settings.productGiftVariantIds.every((variantId) =>
        cart.items.some(
          (item) => item.variant_id === variantId && item.properties?._BrandGwp === 'product' && item.quantity === 1
        )
      );

    document.querySelectorAll('[data-brand-product-gwp]').forEach((element) => {
      element.toggleAttribute('data-qualified', qualifyingProductPresent);
      element.toggleAttribute('data-complete', productGiftsPresent);

      element.querySelectorAll('[data-brand-product-gwp-copy]').forEach((copy) => {
        copy.textContent = productGiftsPresent ? element.dataset.successMessage : element.dataset.offerMessage;
      });

      element.querySelectorAll('[data-brand-product-gwp-kicker]').forEach((kicker) => {
        kicker.textContent = productGiftsPresent ? element.dataset.successKicker : element.dataset.offerKicker;
      });
    });
  };

  const getSectionsToRefresh = () => {
    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    if (mainCartItems?.dataset.id) sections.push(mainCartItems.dataset.id);
    if (mainCartFooter?.dataset.id) sections.push(mainCartFooter.dataset.id);

    return [...new Set(sections)];
  };

  const refreshCartPageSections = (state) => {
    if (!state?.sections) return;

    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');

    if (mainCartItems?.dataset.id && state.sections[mainCartItems.dataset.id]) {
      const html = new DOMParser().parseFromString(state.sections[mainCartItems.dataset.id], 'text/html');
      const source = html.querySelector('cart-items');
      const target = document.querySelector('cart-items');
      if (source && target) target.replaceWith(source);
    }

    if (mainCartFooter?.dataset.id && state.sections[mainCartFooter.dataset.id]) {
      const html = new DOMParser().parseFromString(state.sections[mainCartFooter.dataset.id], 'text/html');
      const source = html.querySelector('#main-cart-footer');
      if (source) mainCartFooter.replaceWith(source);
    }
  };

  const refreshCartDrawerSections = (state) => {
    if (!state?.sections) return;

    const drawerMarkup = state.sections['cart-drawer'];
    const targetDrawer = document.querySelector('cart-drawer');
    if (drawerMarkup && targetDrawer) {
      const html = new DOMParser().parseFromString(drawerMarkup, 'text/html');
      const sourceDrawer = html.querySelector('cart-drawer');
      const sourceContent = sourceDrawer?.querySelector('#CartDrawer');
      const targetContent = targetDrawer.querySelector('#CartDrawer');

      if (sourceDrawer && sourceContent && targetContent) {
        targetContent.innerHTML = sourceContent.innerHTML;
        targetDrawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
        targetContent
          .querySelector('#CartDrawer-Overlay')
          ?.addEventListener('click', targetDrawer.close.bind(targetDrawer));
      }
    }

    const bubbleMarkup = state.sections['cart-icon-bubble'];
    const targetBubble = document.getElementById('cart-icon-bubble');
    if (bubbleMarkup && targetBubble) {
      const html = new DOMParser().parseFromString(bubbleMarkup, 'text/html');
      const sourceBubble = html.getElementById('cart-icon-bubble');
      const sourceSection = html.querySelector('#shopify-section-cart-icon-bubble, .shopify-section');
      if (sourceBubble) {
        targetBubble.innerHTML = sourceBubble.innerHTML;
      } else if (sourceSection) {
        targetBubble.innerHTML = sourceSection.innerHTML;
      }
    }

    if (checkoutLocked) setCheckoutLocked(true);
  };

  const fetchCartMutation = async (url, body) => {
    const response = await fetch(url, {
      ...fetchConfig('javascript'),
      body: JSON.stringify(body),
    });
    const state = await response.json();
    if (!response.ok) {
      throw new Error(state.description || state.message || 'Cart update failed');
    }
    refreshCartPageSections(state);
    refreshCartDrawerSections(state);
    return state;
  };

  const syncThresholdGift = async (cart, settings) => {
    if (!settings.giftEnabled || !settings.giftVariantId || !settings.giftThreshold || syncInFlight) return;

    const qualifyingTotal = getQualifyingTotal(cart, settings.giftVariantId);
    const giftItems = cart.items.filter(
      (item) => item.variant_id === settings.giftVariantId && item.properties?._BrandGwp === 'threshold'
    );
    const shouldHaveGift = qualifyingTotal >= settings.giftThreshold;

    if (shouldHaveGift && giftItems.length === 0) {
      syncInFlight = true;
      await fetchCartMutation(`${routes.cart_add_url}`, {
        items: [
          {
            id: settings.giftVariantId,
            quantity: 1,
            properties: {
              _BrandGwp: 'threshold',
            },
          },
        ],
        sections: getSectionsToRefresh(),
        sections_url: window.location.pathname,
      });
      syncInFlight = false;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-promotions' });
      return;
    }

    if (!shouldHaveGift && giftItems.length > 0) {
      syncInFlight = true;
      const updates = giftItems.reduce((payload, item) => {
        payload[item.key] = 0;
        return payload;
      }, {});

      await fetchCartMutation(`${routes.cart_update_url}`, {
        updates,
        sections: getSectionsToRefresh(),
        sections_url: window.location.pathname,
      });
      syncInFlight = false;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-promotions' });
    }
  };

  const syncProductGift = async (cart, settings) => {
    if (syncInFlight) return false;

    const giftItems = cart.items.filter((item) => item.properties?._BrandGwp === 'product');
    const shouldHaveGifts =
      settings.productGiftEnabled &&
      settings.productGiftVariantIds.length > 0 &&
      hasQualifyingProduct(cart, settings.productGiftQualifyingValue);

    if (!shouldHaveGifts) {
      if (giftItems.length === 0) return false;

      syncInFlight = true;
      const updates = giftItems.reduce((payload, item) => {
        payload[item.key] = 0;
        return payload;
      }, {});

      await fetchCartMutation(`${routes.cart_update_url}`, {
        updates,
        sections: getSectionsToRefresh(),
        sections_url: window.location.pathname,
      });
      syncInFlight = false;
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-promotions' });
      return true;
    }

    const expectedVariantIds = new Set(settings.productGiftVariantIds);
    const seenVariantIds = new Set();
    const needsReset = giftItems.some((item) => {
      const duplicate = seenVariantIds.has(item.variant_id);
      seenVariantIds.add(item.variant_id);
      return !expectedVariantIds.has(item.variant_id) || duplicate || item.quantity !== 1;
    });

    syncInFlight = true;

    if (needsReset) {
      const updates = giftItems.reduce((payload, item) => {
        payload[item.key] = 0;
        return payload;
      }, {});

      await fetchCartMutation(`${routes.cart_update_url}`, {
        updates,
        sections: getSectionsToRefresh(),
        sections_url: window.location.pathname,
      });
      seenVariantIds.clear();
    }

    const variantsToAdd = settings.productGiftVariantIds.filter(
      (variantId) => needsReset || !seenVariantIds.has(variantId)
    );

    if (variantsToAdd.length > 0) {
      await fetchCartMutation(`${routes.cart_add_url}`, {
        items: variantsToAdd.map((variantId) => ({
          id: variantId,
          quantity: 1,
          properties: {
            _BrandGwp: 'product',
          },
        })),
        sections: getSectionsToRefresh(),
        sections_url: window.location.pathname,
      });
    }

    syncInFlight = false;
    const giftsChanged = needsReset || variantsToAdd.length > 0;
    if (giftsChanged) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-promotions' });
    }
    return giftsChanged;
  };

  const syncCartPromotions = async (cartState) => {
    const settings = getSettings();
    if (!settings) return;

    setCheckoutLocked(true);

    try {
      const cart = Array.isArray(cartState?.items)
        ? cartState
        : await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' }).then((response) => response.json());
      updateProgressCopy(cart, settings);
      await syncThresholdGift(cart, settings);
      await syncProductGift(cart, settings);
    } catch (error) {
      console.error('Brand cart promotions failed to sync', error);
    } finally {
      syncInFlight = false;
      setCheckoutLocked(false);
    }
  };

  document.addEventListener('DOMContentLoaded', () => syncCartPromotions());
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === 'brand-cart-promotions') return;
    setCheckoutLocked(true);
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => syncCartPromotions(event.cartData), 0);
  });

  document.addEventListener(
    'submit',
    (event) => {
      if (checkoutLocked && event.submitter?.matches(CHECKOUT_SELECTOR)) {
        event.preventDefault();
      }
    },
    true
  );
})();
